import gql from 'graphql-tag';

// --- Fragments ---
const FORM_FRAGMENT = gql`
  fragment FormFragment on CustomForm {
    id
    title
    description
    slug
    public_url
    embed_code
    is_active
    is_public
    form_type
    restrict_to_students
    target_classroom_section_id
    target_programme_id
    admission_field_mapping
    application_deadline
    payment_gates_review
    declaration_text
    sections {
        id
        title
        description
        order
    }
    stages {
        id
        title
        description
        order
        field_names
        fee_bill_item_template_id
        gate
    }
    branding {
        cover_image_url
        logo_url
        logo_position
        primary_color
        accent_color
        background_color
        background_image_url
        font_family
        layout_preset
        theme_preset
    }
    billItem {
        id
        name
        default_amount
    }
    targetClassroomSection {
        id
        classroom { id name }
    }
    targetProgramme {
        id
        program_name
    }
    reference_requirements {
        key
        label
        description
        count
        reference_form_id
        stage_id
    }
    reference_settings {
        applicants_can_view_responses
        expires_days
    }
    created_at
    updated_at
  }
`;

const FORM_FIELD_FRAGMENT = gql`
  fragment FormFieldFragment on FormField {
    name
    label
    type
    required
    placeholder
    options
    min
    max
    max_size
    allowed_extensions
    order
    section_id
    show_if {
        field
        operator
        value
    }
    age_eligibility {
        min_age
        max_age
        reference_date
        message
    }
    maps_to
    data_source
    show_in_table
  }
`;

export const SAVE_DRAFT = gql`
  mutation SaveDraft($slug: String!, $data: JSON!, $tracking_code: String, $token: String, $references: [ReferenceNominationInput!], $applicant_email: String) {
    saveDraft(slug: $slug, data: $data, tracking_code: $tracking_code, token: $token, references: $references, applicant_email: $applicant_email) {
      id
      status
      current_stage
      tracking_code
      applicant_email
      submitted_at
    }
  }
`;

// Finalizing a draft is the one-shot submit's twin, so it selects the same
// payment fields: a paid form's bill (checkout redirect), payment_waived_at
// (waiver code) and the application PDF link.
export const SUBMIT_STAGE = gql`
  mutation SubmitStage($tracking_code: String!, $token: String!, $waiver_code: String, $email_verification_token: String) {
    submitStage(tracking_code: $tracking_code, token: $token, waiver_code: $waiver_code, email_verification_token: $email_verification_token) {
      id
      status
      current_stage
      tracking_code
      submitted_at
      pdf_url
      payment_waived_at
      bill {
        id
        amount
        status
        billable_id
        billable_type
        billDetails {
          id
          amount
        }
      }
    }
  }
`;

// --- Queries ---
export const GET_BILL_ITEMS = gql`
    query GetFormBillItems {
        billItemTemplates {
            id
            name
            default_amount
        }
    }
`;

export const GET_SCHOOL_PAYMENT_PROVIDER = gql`
    query GetSchoolPaymentProvider($slug: String!) {
        publicPaymentProvider(form_slug: $slug) {
            name
            is_active
            publicKey
        }
    }
`;

export const GET_FORMS = gql`
  query ListForms {
    forms {
        id
        title
        slug
        public_url
        is_active
        is_public
        form_type
        created_at
        submissions(first: 1) {
            paginatorInfo {
                total
            }
        }
    }
  }
`;

export const GET_FORM_DETAILS = gql`
  query GetFormDetails($id: ID!) {
    form(id: $id) {
      ...FormFragment
      fields {
        ...FormFieldFragment
      }
      publicOptions {
        classroomSections { id label }
        programmes { id program_name }
        dataSources {
            source
            options { id label }
        }
      }
    }
  }
  ${FORM_FRAGMENT}
  ${FORM_FIELD_FRAGMENT}
`;

export const GET_FORM_DETAILS_WITH_COUNTS = gql`
  query GetFormDetailsWithCounts($id: ID!) {
    form(id: $id) {
        ...FormFragment
        fields {
            ...FormFieldFragment
        }
        publicOptions {
            classroomSections { id label }
            programmes { id program_name }
            dataSources {
                source
                options { id label }
            }
        }
        submissionCounts {
            total
            draft
            pending
            approved
            rejected
            converted
        }
    }
  }
  ${FORM_FRAGMENT}
  ${FORM_FIELD_FRAGMENT}
`;

export const LIST_FORM_SUBMISSIONS = gql`
  query ListFormSubmissions(
    $form_id: ID!
    $page: Int
    $first: Int
    $status: FormSubmissionStatus
    $search: String
    $assigned_to_me: Boolean
    $assignment: SubmissionAssignmentFilter
  ) {
    formSubmissions(
        form_id: $form_id, page: $page, first: $first,
        status: $status, search: $search,
        assigned_to_me: $assigned_to_me, assignment: $assignment,
    ) {
        data {
            id
            data
            resolved_answers { field_name field_label field_type display raw }
            files { field_name original_name url }
            status
            tracking_code
            applicant_email
            review_note
            converted_student_id
            reviewed_at
            assigned_reviewer_at
            duplicate_of_submission_id
            duplicateOf { id tracking_code status created_at }
            awaiting_payment
            pdf_url
            payment_waived_at
            email_verified_at
            paymentWaivedBy { id name }
            waiverCode { id batch_label generatedBy { id name } }
            convertedStudent { id first_name surname matric_no }
            reviewedBy { id name email }
            assignedReviewer { id name email }
            created_at
            user { id email name }
        }
        paginatorInfo {
            total
            count
            perPage
            currentPage
            lastPage
            hasMorePages
        }
    }
  }
`;

// Reference progress for the visible submissions page — the bulk-convert
// dialog restates it per selected application. Kept out of the list query so
// the main table never depends on the reviewer-only field.
export const LIST_SUBMISSION_REFERENCE_PROGRESS = gql`
  query ListSubmissionReferenceProgress(
    $form_id: ID!
    $page: Int
    $first: Int
    $status: FormSubmissionStatus
    $search: String
    $assigned_to_me: Boolean
  ) {
    formSubmissions(
        form_id: $form_id, page: $page, first: $first,
        status: $status, search: $search, assigned_to_me: $assigned_to_me,
    ) {
        data {
            id
            reference_progress { total_required total_received complete }
        }
    }
  }
`;

export const GET_SUBMISSION_DETAILS = gql`
  query GetSubmissionDetails($id: ID!) {
    formSubmission(id: $id) {
        id
        data
        resolved_answers { field_name field_label field_type display raw }
        files {
            field_name
            field_label
            id
            original_name
            mime_type
            size
            url
            download_url
        }
        status
        reference_progress {
            total_required
            total_received
            complete
            requirements { key label required received }
        }
        referenceRequests {
            id
            requirement_key
            referee_name
            referee_email
            referee_phone
            referee_relationship
            status
            declined_reason
            reminder_count
            invited_at
            last_reminded_at
            submitted_at
            expires_at
            created_at
            answers { label value }
            pdf_url
        }
        tracking_code
        applicant_email
        review_note
        reviewed_at
        assigned_reviewer_at
        ip_address
        user_agent
        created_at
        duplicate_of_submission_id
        duplicateOf { id tracking_code status created_at }
        awaiting_payment
        pdf_url
        payment_waived_at
        paymentWaivedBy { id name }
        convertedStudent { id first_name surname matric_no }
        reviewedBy { id name email }
        assignedReviewer { id name email }
        user { id email name }
        bill {
            id
            amount
            status
        }
        activities {
            id
            type
            content
            old_status
            new_status
            is_visible_to_applicant
            metadata
            resolved_at
            is_editable
            created_at
            user { id name }
            attachments {
                id
                original_name
                mime_type
                size
                url
            }
        }
    }
  }
`;

export const EXPORT_APPLICATION_PDFS = gql`
    mutation ExportApplicationPdfs($submission_ids: [ID!]!) {
        exportApplicationPdfs(submission_ids: $submission_ids) {
            id
            status
            type
        }
    }
`;

export const WAIVE_SUBMISSION_PAYMENT = gql`
    mutation WaiveSubmissionPayment($submission_id: ID!, $reason: String!) {
        waiveSubmissionPayment(submission_id: $submission_id, reason: $reason) {
            id
            awaiting_payment
            payment_waived_at
            paymentWaivedBy { id name }
        }
    }
`;

export const ASSIGN_SUBMISSION_REVIEWER = gql`
    mutation AssignSubmissionReviewer($submission_id: ID!, $user_id: ID) {
        assignSubmissionReviewer(submission_id: $submission_id, user_id: $user_id) {
            id
            assigned_reviewer_at
            assignedReviewer { id name email }
        }
    }
`;

export const UNASSIGN_SUBMISSION_REVIEWER = gql`
    mutation UnassignSubmissionReviewer($submission_id: ID!) {
        unassignSubmissionReviewer(submission_id: $submission_id) {
            id
            assigned_reviewer_at
            assignedReviewer { id name email }
        }
    }
`;

export const GET_PUBLIC_FORM = gql`
    query GetPublicForm($slug: String!) {
        publicForm(slug: $slug) {
            id
            title
            description
            form_type
            admission_field_mapping
            application_deadline
            declaration_text
            reference_requirements {
                key
                label
                description
                count
                reference_form_id
                stage_id
            }
            school { slug name logo }
            billItem {
                id
                name
                default_amount
            }
            fields {
                ...FormFieldFragment
            }
            sections {
                id
                title
                description
                order
            }
            stages {
                id
                title
                description
                order
                field_names
                fee_bill_item_template_id
                gate
            }
            branding {
                cover_image_url
                logo_url
                logo_position
                primary_color
                accent_color
                background_color
                background_image_url
                font_family
                layout_preset
                theme_preset
            }
            publicOptions {
                classroomSections { id label }
                programmes { id program_name }
                dataSources {
                    source
                    options { id label }
                }
            }
        }
    }
    ${FORM_FIELD_FRAGMENT}
`;

// --- Mutations ---
export const CREATE_FORM = gql`
    mutation CreateForm($input: CreateFormInput!) {
        createForm(input: $input) {
            ...FormFragment
        }
    }
    ${FORM_FRAGMENT}
`;

export const UPDATE_FORM = gql`
    mutation UpdateForm($input: UpdateFormInput!) {
        updateForm(input: $input) {
            ...FormFragment
        }
    }
    ${FORM_FRAGMENT}
`;

export const DELETE_FORM = gql`
    mutation DeleteForm($id: ID!) {
        deleteForm(id: $id)
    }
`;

export const VALIDATE_FORM_WAIVER_CODE = gql`
    mutation ValidateFormWaiverCode($slug: String!, $code: String!) {
        validateFormWaiverCode(slug: $slug, code: $code)
    }
`;

export const REQUEST_FORM_EMAIL_VERIFICATION = gql`
    mutation RequestFormEmailVerification($slug: String!, $email: String!) {
        requestFormEmailVerification(slug: $slug, email: $email)
    }
`;

export const VERIFY_FORM_EMAIL_OTP = gql`
    mutation VerifyFormEmailOtp($slug: String!, $email: String!, $otp: String!) {
        verifyFormEmailOtp(slug: $slug, email: $email, otp: $otp) {
            token
            expires_at
        }
    }
`;

export const SUBMIT_PUBLIC_FORM = gql`
    mutation SubmitPublicForm($slug: String!, $data: JSON!, $references: [ReferenceNominationInput!], $waiver_code: String, $email_verification_token: String) {
        submitPublicForm(slug: $slug, data: $data, references: $references, waiver_code: $waiver_code, email_verification_token: $email_verification_token) {
            id
            tracking_code
            pdf_url
            payment_waived_at
            bill {
                id
                amount
                status
                billable_id
                billable_type
                billDetails {
                    id
                    amount
                }
            }
        }
    }
`;

export const UPLOAD_FILE = gql`
    mutation UploadFile($file: Upload!) {
        uploadFile(file: $file) {
            id
            url
            original_name
            mime_type
        }
    }
`;

export const UPLOAD_PUBLIC_FORM_FILE = gql`
    mutation UploadPublicFormFile($slug: String!, $field_name: String!, $file: Upload!) {
        uploadPublicFormFile(slug: $slug, field_name: $field_name, file: $file) {
            id
            url
            original_name
            mime_type
            size
        }
    }
`;

export const UPLOAD_REFERENCE_FILE = gql`
    mutation UploadReferenceFile($token: String!, $field_name: String!, $file: Upload!) {
        uploadReferenceFile(token: $token, field_name: $field_name, file: $file) {
            id
            url
            original_name
            mime_type
            size
        }
    }
`;

export const INITIATE_PUBLIC_PAYMENT = gql`
    mutation InitiatePublicPayment($bill_id: ID!, $processor: String!, $payment_method: String) {
        initiatePublicPayment(
            bill_id: $bill_id
            processor: $processor
            payment_method: $payment_method
        ) {
            paymentUrl
            rrr
            hash
            convenienceFee
            baseAmount
            totalAmount
            merchantCode
            payItemId
            transactionReference
            amountInKobo
            currency
            customerEmail
            # cust_id / cust_name: what Interswitch echoes back on the school's
            # settlement report. Without them a guest's application fee lands
            # there anonymous and has to be reconciled by reference.
            customerId
            customerName
            inlineCheckoutUrl
            mode
            splitAccounts
        }
    }
`;

export const RESOLVE_PUBLIC_PAYMENT = gql`
    mutation ResolvePublicPayment($order_id: String!, $processor: String, $transaction_id: String) {
        resolvePublicPayment(order_id: $order_id, processor: $processor, transaction_id: $transaction_id) {
            id
            status
            amount
        }
    }
`;

export const REVIEW_FORM_SUBMISSION = gql`
    mutation ReviewFormSubmission($submission_id: ID!, $status: FormSubmissionStatus!, $note: String) {
        reviewFormSubmission(submission_id: $submission_id, status: $status, note: $note) {
            id
            status
            review_note
            reviewed_at
            reviewedBy { id name }
        }
    }
`;

export const ADD_SUBMISSION_NOTE = gql`
    mutation AddSubmissionNote(
        $submission_id: ID!
        $content: String!
        $type: SubmissionActivityType
        $is_visible_to_applicant: Boolean
        $metadata: JSON
    ) {
        addSubmissionNote(
            submission_id: $submission_id
            content: $content
            type: $type
            is_visible_to_applicant: $is_visible_to_applicant
            metadata: $metadata
        ) {
            id
            type
            content
            is_visible_to_applicant
            metadata
            created_at
        }
    }
`;

export const UPDATE_SUBMISSION_NOTE = gql`
    mutation UpdateSubmissionNote(
        $id: ID!
        $content: String
        $is_visible_to_applicant: Boolean
    ) {
        updateSubmissionNote(
            id: $id
            content: $content
            is_visible_to_applicant: $is_visible_to_applicant
        ) {
            id
            type
            content
            is_visible_to_applicant
            metadata
            resolved_at
            is_editable
            created_at
            user { id name }
        }
    }
`;

export const DELETE_SUBMISSION_NOTE = gql`
    mutation DeleteSubmissionNote($id: ID!) {
        deleteSubmissionNote(id: $id)
    }
`;

export const GET_APPLICANT_STATUS = gql`
    query ApplicantStatus($tracking_code: String!) {
        applicantStatus(tracking_code: $tracking_code) {
            tracking_code
            form_title
            form_type
            status
            submitted_at
            reviewed_at
            review_note
            activities {
                id
                type
                content
                old_status
                new_status
                is_visible_to_applicant
                resolved_at
                created_at
            }
            has_bill
            bill_status
            bill_amount
            payment_state
        }
    }
`;

export const CONVERT_SUBMISSION_TO_STUDENT = gql`
    mutation ConvertSubmissionToStudent(
        $submission_id: ID!
        $field_mapping: JSON!
        $classroom_section_id: ID
        $programme_id: Int
        $matric_no: String
        $campus_id: ID
        $target_school_id: ID
    ) {
        convertSubmissionToStudent(
            submission_id: $submission_id
            field_mapping: $field_mapping
            classroom_section_id: $classroom_section_id
            programme_id: $programme_id
            matric_no: $matric_no
            campus_id: $campus_id
            target_school_id: $target_school_id
        ) {
            id
            first_name
            surname
            matric_no
        }
    }
`;

// The programme the applicant chose, resolved by the server the way conversion
// and the admission letter read it — the convert dialog pre-fills from this
// instead of guessing from PROGRAMME_SELECT fields alone.
export const GET_APPLICANT_PROGRAMME = gql`
    query ApplicantProgramme($id: ID!) {
        formSubmission(id: $id) {
            id
            applicant_programme {
                programme_id
                answer
            }
        }
    }
`;

export const GET_CHILD_SCHOOL_CONVERSION_OPTIONS = gql`
    query ChildSchoolConversionOptions($school_id: ID!) {
        childSchoolConversionOptions(school_id: $school_id) {
            classroom_sections {
                id
                name
                classroom {
                    id
                    name
                }
                section {
                    id
                    name
                }
            }
            programmes {
                id
                program_name
            }
            campuses {
                id
                name
            }
            has_auto_matric
        }
    }
`;

export const GET_STUDENT_FORMS = gql`
    query StudentForms($first: Int = 20, $page: Int = 1) {
        studentForms(first: $first, page: $page) {
            data {
                id
                title
                description
                form_type
                billItem { id name default_amount }
                fields { ...FormFieldFragment }
                my_draft { id updated_at }
            }
            paginatorInfo {
                total
                currentPage
                lastPage
                hasMorePages
            }
        }
    }
    ${FORM_FIELD_FRAGMENT}
`;

// The student's own parked draft of a form: answers to prefill plus the
// pending referees they nominated, so a reopened form resends them.
export const STUDENT_FORM_DRAFT = gql`
    query StudentFormDraft($id: ID!) {
        form(id: $id) {
            id
            my_draft {
                id
                status
                data
                updated_at
                draft_nominations { requirement_key name email phone relationship }
            }
        }
    }
`;

export const SAVE_STUDENT_FORM_DRAFT = gql`
    mutation SaveStudentFormDraft($form_id: ID!, $data: JSON!, $references: [ReferenceNominationInput!]) {
        saveStudentFormDraft(form_id: $form_id, data: $data, references: $references) {
            id
            status
            data
            updated_at
        }
    }
`;

export const SUBMIT_FORM = gql`
    mutation SubmitForm($form_id: ID!, $data: JSON!, $references: [ReferenceNominationInput!], $waiver_code: String) {
        submitForm(form_id: $form_id, data: $data, references: $references, waiver_code: $waiver_code) {
            id
            data
            status
            tracking_code
            created_at
            bill {
                id
                amount
                status
                billDetails {
                    id
                    amount
                }
            }
        }
    }
`;

export const EXPORT_FORM_SUBMISSIONS = gql`
    query ExportFormSubmissions($form_id: ID!, $status: FormSubmissionStatus) {
        exportFormSubmissions(form_id: $form_id, status: $status) {
            headers
            rows
            filename
        }
    }
`;

export const BULK_CONVERT_SUBMISSIONS = gql`
    mutation BulkConvertSubmissions(
        $submission_ids: [ID!]!
        $field_mapping: JSON!
        $classroom_section_id: ID
        $programme_id: Int
        $campus_id: ID
        $target_school_id: ID
    ) {
        bulkConvertSubmissions(
            submission_ids: $submission_ids
            field_mapping: $field_mapping
            classroom_section_id: $classroom_section_id
            programme_id: $programme_id
            campus_id: $campus_id
            target_school_id: $target_school_id
        ) {
            converted
            failed
            errors
        }
    }
`;

// --- Applicant OTP & Status ---
// Scope by school_slug (email links carry ?school=) OR tracking_code — the
// applicant always knows their tracking code, rarely their school's slug.
export const REQUEST_APPLICANT_OTP = gql`
    mutation RequestApplicantOtp($email: String!, $school_slug: String, $tracking_code: String) {
        requestApplicantOtp(email: $email, school_slug: $school_slug, tracking_code: $tracking_code)
    }
`;

export const VERIFY_APPLICANT_OTP = gql`
    mutation VerifyApplicantOtp($email: String!, $otp: String!, $school_slug: String, $tracking_code: String) {
        verifyApplicantOtp(email: $email, otp: $otp, school_slug: $school_slug, tracking_code: $tracking_code) {
            token
            expires_at
        }
    }
`;

export const GET_APPLICANT_SUBMISSIONS = gql`
    query ApplicantSubmissions($token: String!) {
        applicantSubmissions(token: $token) {
            tracking_code
            form_title
            references {
                key
                label
                required
                received
                referees {
                    id
                    name
                    email
                    status
                    submitted_at
                    answers { label value }
                }
            }
            form_type
            form_slug
            data
            current_stage
            status
            submitted_at
            reviewed_at
            review_note
            pdf_url
            admission_letter_url
            activities {
                id
                type
                content
                old_status
                new_status
                is_visible_to_applicant
                resolved_at
                created_at
                metadata
                attachments {
                    id
                    original_name
                    mime_type
                    size
                    url
                }
            }
            has_bill
            bill {
                id
                amount
                status
                billable_id
                billable_type
                details { id amount }
            }
            bill_status
            bill_amount
            payment_state
            has_action_required
        }
    }
`;

export const APPLICANT_REPLY = gql`
    mutation ApplicantReply(
        $tracking_code: String!
        $content: String!
        $token: String!
        $file_ids: [ID!]
        $activity_id: ID
    ) {
        applicantReply(
            tracking_code: $tracking_code
            content: $content
            token: $token
            file_ids: $file_ids
            activity_id: $activity_id
        ) {
            id
            type
            content
            created_at
        }
    }
`;

export const APPLICANT_UPLOAD_FILE = gql`
    mutation ApplicantUploadFile($token: String!, $file: Upload!) {
        applicantUploadFile(token: $token, file: $file) {
            id
            original_name
            mime_type
            size
            url
        }
    }
`;

export const APPLICANT_UPDATE_FIELD = gql`
    mutation ApplicantUpdateField(
        $tracking_code: String!
        $token: String!
        $activity_id: ID!
        $new_value: JSON
    ) {
        applicantUpdateField(
            tracking_code: $tracking_code
            token: $token
            activity_id: $activity_id
            new_value: $new_value
        ) {
            id
            type
            content
            created_at
        }
    }
`;

// --- Referencing (a reference is a form, filled by someone else for the applicant) ---
export const GET_REFERENCE_REQUEST = gql`
    query GetReferenceRequest($token: String!) {
        referenceRequest(token: $token) {
            status
            referee_name
            applicant_name
            form_title
            school_name
            expires_at
            applicant_can_view
            draft_data
            referenceForm {
                id
                title
                description
                fields { ...FormFieldFragment }
                sections { id title description order }
                branding {
                    cover_image_url
                    logo_url
                    logo_position
                    primary_color
                    accent_color
                    background_color
                    background_image_url
                    font_family
                    layout_preset
                    theme_preset
                }
                publicOptions {
                    classroomSections { id label }
                    programmes { id program_name }
                    dataSources { source options { id label } }
                }
            }
        }
    }
    ${FORM_FIELD_FRAGMENT}
`;

export const SUBMIT_REFERENCE = gql`
    mutation SubmitReference($token: String!, $data: JSON!) {
        submitReference(token: $token, data: $data)
    }
`;

export const DECLINE_REFERENCE = gql`
    mutation DeclineReference($token: String!, $reason: String) {
        declineReference(token: $token, reason: $reason)
    }
`;

export const SAVE_REFERENCE_DRAFT = gql`
    mutation SaveReferenceDraft($token: String!, $data: JSON!) {
        saveReferenceDraft(token: $token, data: $data)
    }
`;

export const REPLACE_REFERENCE_REFEREE = gql`
    mutation ReplaceReferenceReferee(
        $tracking_code: String!, $token: String!, $request_id: ID!,
        $name: String!, $email: String!, $phone: String, $relationship: String
    ) {
        replaceReferenceReferee(
            tracking_code: $tracking_code, token: $token, request_id: $request_id,
            name: $name, email: $email, phone: $phone, relationship: $relationship
        )
    }
`;

export const ADD_REFERENCE_REQUEST = gql`
    mutation AddReferenceRequest(
        $submission_id: ID!, $requirement_key: String!,
        $referee_name: String!, $referee_email: String!,
        $referee_phone: String, $referee_relationship: String
    ) {
        addReferenceRequest(
            submission_id: $submission_id, requirement_key: $requirement_key,
            referee_name: $referee_name, referee_email: $referee_email,
            referee_phone: $referee_phone, referee_relationship: $referee_relationship
        ) { id status referee_name referee_email }
    }
`;

export const CANCEL_REFERENCE_REQUEST = gql`
    mutation CancelReferenceRequest($id: ID!) {
        cancelReferenceRequest(id: $id) { id status }
    }
`;

export const UPDATE_REFERENCE_REQUEST = gql`
    mutation UpdateReferenceRequest($id: ID!, $referee_name: String, $referee_email: String) {
        updateReferenceRequest(id: $id, referee_name: $referee_name, referee_email: $referee_email) {
            id referee_name referee_email status
        }
    }
`;

export const RESEND_REFERENCE_INVITE = gql`
    mutation ResendReferenceInvite($id: ID!) {
        resendReferenceInvite(id: $id) { id status invited_at expires_at }
    }
`;

// --- Waiver Code Management (admin) ---
export const FORM_WAIVER_CODES = gql`
    query FormWaiverCodes(
        $form_id: ID!
        $academic_calendar_id: ID
        $status: FormWaiverCodeStatus
        $first: Int
        $page: Int
    ) {
        formWaiverCodes(
            form_id: $form_id
            academic_calendar_id: $academic_calendar_id
            status: $status
            first: $first
            page: $page
        ) {
            data {
                id
                form_id
                academic_calendar_id
                status
                batch_label
                used_by_email
                used_at
                revoked_at
                generatedBy { id name }
                academicCalendar { id session { name } termStructure { name } }
                usedBySubmission { id tracking_code }
                revokedBy { id name }
                created_at
                campus { id name }
            }
            paginatorInfo {
                currentPage
                lastPage
                total
            }
        }
    }
`;

export const GENERATE_FORM_WAIVER_CODES = gql`
    mutation GenerateFormWaiverCodes(
        $form_id: ID!
        $academic_calendar_id: ID!
        $quantity: Int!
        $batch_label: String
        $campus_id: ID
    ) {
        generateFormWaiverCodes(
            form_id: $form_id
            academic_calendar_id: $academic_calendar_id
            quantity: $quantity
            batch_label: $batch_label
            campus_id: $campus_id
        ) {
            codes
            count
        }
    }
`;

export const REVOKE_FORM_WAIVER_CODE = gql`
    mutation RevokeFormWaiverCode($id: ID!) {
        revokeFormWaiverCode(id: $id) {
            id
            status
            revoked_at
        }
    }
`;

export const REVOKE_ALL_FORM_WAIVER_CODES = gql`
    mutation RevokeAllFormWaiverCodes($form_id: ID!, $batch_label: String, $academic_calendar_id: ID) {
        revokeAllFormWaiverCodes(form_id: $form_id, batch_label: $batch_label, academic_calendar_id: $academic_calendar_id)
    }
`;

// --- Waiver codes across the school (the Bursar's page) ---
const WAIVER_CODE_ROW = gql`
    fragment WaiverCodeRow on FormWaiverCode {
        id
        status
        batch_label
        used_by_email
        used_at
        revoked_at
        created_at
        bill_amount_paid
        campus { id name }
        form { id title }
        generatedBy { id name }
        academicCalendar { id session { name } termStructure { name } }
        usedBySubmission { id tracking_code applicant_name }
        revokedBy { id name }
    }
`;

export const WAIVER_CODES = gql`
    query WaiverCodes(
        $form_id: ID
        $campus_id: ID
        $without_campus: Boolean
        $academic_calendar_id: ID
        $status: FormWaiverCodeStatus
        $batch_label: String
        $paid_after_waiver: Boolean
        $first: Int
        $page: Int
    ) {
        waiverCodes(
            form_id: $form_id
            campus_id: $campus_id
            without_campus: $without_campus
            academic_calendar_id: $academic_calendar_id
            status: $status
            batch_label: $batch_label
            paid_after_waiver: $paid_after_waiver
            first: $first
            page: $page
        ) {
            data { ...WaiverCodeRow }
            paginatorInfo { currentPage lastPage total }
        }
    }
    ${WAIVER_CODE_ROW}
`;

export const WAIVER_CODE_STATS = gql`
    query WaiverCodeStats($form_id: ID, $academic_calendar_id: ID) {
        waiverCodeStats(form_id: $form_id, academic_calendar_id: $academic_calendar_id) {
            total
            available
            used
            revoked
            paid_after_waiver
            campuses { campus_id campus_name total available used revoked }
            forms { form_id form_title total available used revoked }
        }
    }
`;

export const LOOKUP_WAIVER_CODE = gql`
    query LookupWaiverCode($code: String!) {
        lookupWaiverCode(code: $code) { ...WaiverCodeRow }
    }
    ${WAIVER_CODE_ROW}
`;

export const SET_WAIVER_CODES_CAMPUS = gql`
    mutation SetWaiverCodesCampus($ids: [ID!]!, $campus_id: ID) {
        setWaiverCodesCampus(ids: $ids, campus_id: $campus_id)
    }
`;

// Public: the applicant enters a waiver code on the payment page.
export const REDEEM_WAIVER_CODE_AT_CHECKOUT = gql`
    mutation RedeemWaiverCodeAtCheckout($bill_id: ID!, $tracking_code: String!, $code: String!) {
        redeemWaiverCodeAtCheckout(bill_id: $bill_id, tracking_code: $tracking_code, code: $code) {
            tracking_code
            payment_waived_at
        }
    }
`;

export const FORM_WAIVER_CODE_SUMMARY = gql`
    query FormWaiverCodeSummary($form_id: ID!) {
        form(id: $form_id) {
            id
            waiverCodeSummary {
                total
                available
                used
                revoked
            }
        }
    }
`;
